import clsx from 'clsx';
import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { EventTicketGroupBody } from 'src/components/Accordions/TicketGroupsEventsAccordion/EventTicketGroupBody';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { MultiSelectActionBar } from 'src/components/common/MultiSelect/MultiSelectActionBar';
import { multiActionToolBarNoHorizontalPadding } from 'src/components/Events/EventPage/EventPage.css';
import { PurchasesBulkActionsPermissions } from 'src/components/Purchases/BulkActions/PurchasesBulkActionsPermissions';
import { PurchasesTileBulkActions } from 'src/components/Purchases/BulkActions/PurchasesTileBulkActions/PurchasesTileBulkActions';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  EventWithData,
  Feature,
  PurchaseOrderTicketGroup,
} from 'src/WebApiController';

import { MinRowsForUsingVirtuoso } from '../constants';
import {
  EventAccordionItemProps,
  EventsAccordion,
  VirtualizedEventAccordionProps,
} from '../EventsAccordion';
import { EventsAccordionV2 } from '../EventsAccordion/EventsAccordionV2';
import { EventTicketGroupHeader } from './EventTicketGroupHeader';

export function translateEventToTicketGroupAccordionItem(
  props: EventWithData,
  index: number,
  noPadding?: boolean
): EventAccordionItemProps<PurchaseOrderTicketGroup> {
  return {
    index,
    itemId: props.event.virtId,
    HeaderComponent: EventTicketGroupHeader,
    BodyComponent: EventTicketGroupBody,
    AfterHeaderComponent: ({
      displayMode,
    }: {
      displayMode?:
        | 'full'
        | 'count-only'
        | 'count-only-button'
        | 'checkbox-only';
    }) => (
      <PurchasesBulkActionsPermissions>
        <MultiSelectActionBar
          groupId={props.event.virtId}
          className={clsx({
            [multiActionToolBarNoHorizontalPadding]: noPadding,
          })}
          displayMode={displayMode}
        >
          <PurchasesTileBulkActions groupId={props.event.virtId} isEventView />
        </MultiSelectActionBar>
      </PurchasesBulkActionsPermissions>
    ),
    usingVirtuoso: (props.counts.tktGrpsCnt ?? 0) > MinRowsForUsingVirtuoso,
    ...props,
    entities: props.entities.ticketGroups ?? [],
    entitiesSummary: props.entities.ticketGroupsSummary ?? null,
    entityCount: props.counts.tktGrpsCnt ?? 0,
  };
}

export function TicketGroupsEventsAccordion(
  props: VirtualizedEventAccordionProps<PurchaseOrderTicketGroup>
) {
  const { allGroupsIds, setAllGroupsIds } = useMultiSelectionContext();
  const hasPurchaseTableV2Feature = useUserHasFeature(Feature.PurchaseTableV2);
  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    const itemsIds = props.items.map((item) => item.event.virtId);
    if (!isEqual(allGroupsIds, itemsIds)) {
      setAllGroupsIds(itemsIds);
    }
  }, [allGroupsIds, props.items, setAllGroupsIds]);

  return hasPurchaseTableV2Feature ? (
    <EventsAccordionV2 {...props} />
  ) : (
    <EventsAccordion {...props} />
  );
}

export function TicketGroupEventsEmpty() {
  return (
    <EmptySectionContent.Root
      icon={
        <EmptySectionContent.SolidIconContainer>
          <SearchSolidIcon />
        </EmptySectionContent.SolidIconContainer>
      }
    >
      <EmptySectionContent.Label>
        <Content id={ContentId.NoResultFound} />
      </EmptySectionContent.Label>
      <EmptySectionContent.DetailMessage>
        <Content id={ContentId.NoEventsMatching} />
      </EmptySectionContent.DetailMessage>
    </EmptySectionContent.Root>
  );
}
